import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  createSmsChannelAccount,
  getSmsChannelAccountSettings,
  testSmsChannelAccountConnection,
  updateSmsChannelAccountCredentials,
} from '../services/channelAccountService';
import { ChannelAccountSmsSettings, CallingConnectionTestResult } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';

const emptySmsSettingsForm = {
  displayName: 'SMS',
  apiUrl: '',
  httpMethod: 'POST',
  contentType: 'application/json',
  bodyTemplate: '{"number":"{to}","message":"{text}","sender":"{sender}"}',
  authHeaderName: '',
  authHeaderValue: '',
  basicAuthUsername: '',
  basicAuthPassword: '',
  senderName: '',
  successContains: '',
  outboundEnabled: true,
};

interface SmsConnectorTabProps {
  canManage: boolean;
}

export const SmsConnectorTab: React.FC<SmsConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin', 'calling']);
  const { showToast } = useToast();
  const smsChannelAccounts = useChannelAccounts('Sms', true);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [testingConnection, setTestingConnection] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountSmsSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState(emptySmsSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [latestTestResult, setLatestTestResult] = React.useState<CallingConnectionTestResult | null>(null);
  const [showAddAccount, setShowAddAccount] = React.useState(false);
  const [testNumber, setTestNumber] = React.useState('');

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getSmsChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestTestResult(null);
      setSettingsForm({
        displayName: 'SMS',
        apiUrl: data.apiUrl || '',
        httpMethod: data.httpMethod || 'POST',
        contentType: data.contentType || 'application/json',
        bodyTemplate: data.bodyTemplate || '',
        authHeaderName: data.authHeaderName || '',
        authHeaderValue: '',
        basicAuthUsername: data.basicAuthUsername || '',
        basicAuthPassword: '',
        senderName: data.senderName || '',
        successContains: data.successContains || '',
        outboundEnabled: data.outboundEnabled,
      });
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadSmsSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = smsChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSettingsForm(emptySmsSettingsForm);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, smsChannelAccounts.selectedAccountId]);

  const saveSettings = async () => {
    const selectedAccount = smsChannelAccounts.selectedAccount;
    const isInitialSetup = smsChannelAccounts.accounts.length === 0;

    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (isInitialSetup && !settingsForm.apiUrl.trim()) {
      const message = t('smsGatewayUrlRequired');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      if (isInitialSetup) {
        await createSmsChannelAccount({
          displayName: settingsForm.displayName.trim() || 'SMS',
          apiUrl: settingsForm.apiUrl.trim(),
          httpMethod: settingsForm.httpMethod,
          contentType: settingsForm.contentType.trim() || undefined,
          bodyTemplate: settingsForm.bodyTemplate.trim() || undefined,
          authHeaderName: settingsForm.authHeaderName.trim() || undefined,
          authHeaderValue: settingsForm.authHeaderValue.trim() || undefined,
          basicAuthUsername: settingsForm.basicAuthUsername.trim() || undefined,
          basicAuthPassword: settingsForm.basicAuthPassword.trim() || undefined,
          senderName: settingsForm.senderName.trim() || undefined,
          successContains: settingsForm.successContains.trim() || undefined,
          outboundEnabled: settingsForm.outboundEnabled,
          isDefault: true,
        });
        await smsChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateSmsChannelAccountCredentials(selectedAccount.id, {
          apiUrl: settingsForm.apiUrl.trim() || undefined,
          httpMethod: settingsForm.httpMethod,
          contentType: settingsForm.contentType.trim() || undefined,
          bodyTemplate: settingsForm.bodyTemplate,
          authHeaderName: settingsForm.authHeaderName,
          authHeaderValue: settingsForm.authHeaderValue.trim() || undefined,
          basicAuthUsername: settingsForm.basicAuthUsername,
          basicAuthPassword: settingsForm.basicAuthPassword.trim() || undefined,
          senderName: settingsForm.senderName,
          successContains: settingsForm.successContains,
          outboundEnabled: settingsForm.outboundEnabled,
        });
        await loadAccountSettings(selectedAccount.id);
        await smsChannelAccounts.reload();
      }

      setSettingsForm(prev => ({ ...prev, authHeaderValue: '', basicAuthPassword: '' }));
      setSettingsNotice(t('smsSettingsSaved'));
      showToast(t('smsSettingsSaved'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveSmsSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    const selectedAccount = smsChannelAccounts.selectedAccount;
    if (!selectedAccount || !testNumber.trim()) {
      return;
    }

    setTestingConnection(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await testSmsChannelAccountConnection(selectedAccount.id, testNumber.trim());
      setLatestTestResult(result);
      if (result.succeeded) {
        setSettingsNotice(result.message || t('smsConnectionSucceeded'));
        showToast(result.message || t('smsConnectionSucceeded'), 'success');
      } else {
        const message = result.message || t('failedToTestSmsConnection');
        setSettingsError(message);
        showToast(message, 'error');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToTestSmsConnection');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <section className="company-card">
      <ChannelAccountBar
        channel="Sms"
        accounts={smsChannelAccounts.accounts}
        limits={smsChannelAccounts.limits}
        loading={smsChannelAccounts.loading}
        error={smsChannelAccounts.error}
        selectedAccountId={smsChannelAccounts.selectedAccountId}
        onSelectAccount={smsChannelAccounts.setSelectedAccountId}
        onRefresh={() => { void smsChannelAccounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddAccount}
        onToggleAddForm={() => setShowAddAccount(current => !current)}
        onAccountUpdated={() => { void smsChannelAccounts.reload(); }}
      />

      {showAddAccount && canManage ? (
        <div className="company-form-grid" style={{ marginBottom: 16 }}>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('channelAccountAddSmsHelp')}
          </p>
        </div>
      ) : null}

      {smsChannelAccounts.loading && smsChannelAccounts.accounts.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <h3>{t('smsIntegrationHeading')}</h3>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('smsIntegrationHelp')}
          </p>

          {settingsError ? <div className="error">{settingsError}</div> : null}
          {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

          {loadingSettings ? <Spinner /> : (
            <div className="company-form-grid">
              <label>
                {t('smsGatewayUrl')}
                <input
                  className="text-input"
                  value={settingsForm.apiUrl}
                  onChange={event => setSettingsForm(prev => ({ ...prev, apiUrl: event.target.value }))}
                  placeholder="https://sms.provider.az/api/send"
                  disabled={!canManage}
                />
              </label>
              <label>
                {t('smsHttpMethod')}
                <select
                  className="text-input"
                  value={settingsForm.httpMethod}
                  onChange={event => setSettingsForm(prev => ({ ...prev, httpMethod: event.target.value }))}
                  disabled={!canManage}
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>
              </label>
              <label>
                {t('smsSenderName')}
                <input
                  className="text-input"
                  value={settingsForm.senderName}
                  onChange={event => setSettingsForm(prev => ({ ...prev, senderName: event.target.value }))}
                  placeholder="SIMAIZEER"
                  disabled={!canManage}
                />
              </label>
              <label>
                {t('smsContentType')}
                <input
                  className="text-input"
                  value={settingsForm.contentType}
                  onChange={event => setSettingsForm(prev => ({ ...prev, contentType: event.target.value }))}
                  placeholder="application/json"
                  disabled={!canManage || settingsForm.httpMethod === 'GET'}
                />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                {t('smsBodyTemplate')}
                <textarea
                  className="text-input"
                  rows={3}
                  value={settingsForm.bodyTemplate}
                  onChange={event => setSettingsForm(prev => ({ ...prev, bodyTemplate: event.target.value }))}
                  placeholder='{"number":"{to}","message":"{text}"}'
                  disabled={!canManage || settingsForm.httpMethod === 'GET'}
                />
                <span style={{ color: '#516173', fontSize: '0.8rem' }}>{t('smsTemplateHint')}</span>
              </label>
              <label>
                {t('smsAuthHeaderName')}
                <input
                  className="text-input"
                  value={settingsForm.authHeaderName}
                  onChange={event => setSettingsForm(prev => ({ ...prev, authHeaderName: event.target.value }))}
                  placeholder="Authorization"
                  disabled={!canManage}
                />
              </label>
              <label>
                {t('smsAuthHeaderValue')}
                <input
                  className="text-input"
                  type="password"
                  value={settingsForm.authHeaderValue}
                  onChange={event => setSettingsForm(prev => ({ ...prev, authHeaderValue: event.target.value }))}
                  placeholder={accountSettings?.hasAuthHeaderValue ? t('smsCredentialConfigured') : ''}
                  disabled={!canManage}
                  autoComplete="new-password"
                />
              </label>
              <label>
                {t('smsBasicAuthUsername')}
                <input
                  className="text-input"
                  value={settingsForm.basicAuthUsername}
                  onChange={event => setSettingsForm(prev => ({ ...prev, basicAuthUsername: event.target.value }))}
                  disabled={!canManage}
                  autoComplete="off"
                />
              </label>
              <label>
                {t('smsBasicAuthPassword')}
                <input
                  className="text-input"
                  type="password"
                  value={settingsForm.basicAuthPassword}
                  onChange={event => setSettingsForm(prev => ({ ...prev, basicAuthPassword: event.target.value }))}
                  placeholder={accountSettings?.hasBasicAuthPassword ? t('smsCredentialConfigured') : ''}
                  disabled={!canManage}
                  autoComplete="new-password"
                />
              </label>
              <label>
                {t('smsSuccessContains')}
                <input
                  className="text-input"
                  value={settingsForm.successContains}
                  onChange={event => setSettingsForm(prev => ({ ...prev, successContains: event.target.value }))}
                  placeholder="OK"
                  disabled={!canManage}
                />
              </label>
              <ToggleField
                label={t('smsOutboundEnabled')}
                checked={settingsForm.outboundEnabled}
                onChange={value => setSettingsForm(prev => ({ ...prev, outboundEnabled: value }))}
                disabled={!canManage}
              />
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <p>{t('smsAuthHeaderValue')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasAuthHeaderValue)} /></p>
            <p>{t('smsBasicAuthPassword')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasBasicAuthPassword)} /></p>
          </div>

          {latestTestResult ? (
            <p style={{ color: latestTestResult.succeeded ? '#0f7b3d' : '#b42318', marginTop: 12 }}>
              {latestTestResult.message}
            </p>
          ) : null}

          {canManage ? (
            <div className="company-actions-inline" style={{ marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { void saveSettings(); }}
                disabled={savingSettings || loadingSettings}
              >
                {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveSmsSettings')}
              </button>
              <input
                className="text-input"
                style={{ maxWidth: 200 }}
                value={testNumber}
                onChange={event => setTestNumber(event.target.value)}
                placeholder={t('smsTestNumberPlaceholder')}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { void testConnection(); }}
                disabled={testingConnection || !smsChannelAccounts.selectedAccount || !testNumber.trim()}
              >
                {testingConnection ? t('savingEllipsis', { ns: 'admin' }) : t('testSmsConnection')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
